import { diagnosticJobKinds } from "@/api/adminDiagnosticJob";
import { storageBackendLabel } from "@/components/dataset/storageBackendText";
import type {
  WorkerCurrentJob,
  WorkerCurrentJobKind,
  WorkerDiskUsage,
  WorkerLoadAvg,
  WorkerJobProgress,
} from "@/api";
import { formatBytes } from "@/utils";
import { formatDateTimeSeconds, formatDuration, parseUtcMs } from "@/utils/datetime";
import { diagnosticJobKindLabel } from "./diagnosticJobKindLabel";

// 알려진 job kind는 표시 라벨로 바꾸고, 그 밖의 kind는 raw 문자열로 표시한다.
const JOB_KIND_LABELS: Record<WorkerCurrentJobKind, string> = {
  upload: "Upload",
  merge: "Merge",
  annotation_materialize: "Annotation materialize",
  annotation_bundle_index: "Annotation bundle index",
  storage_cleanup: "Storage cleanup",
  diagnostics: "Diagnostics",
};

export function formatJobKind(kind: string): string {
  return JOB_KIND_LABELS[kind as WorkerCurrentJobKind] ?? kind;
}

// 현재 작업의 종류 문구다. registry 가 작업 정보(subject)를 채웠으면 그 종류를 쓴다. 진단 종류는
// 진단 큐와 같은 문구다. 작업 정보가 없으면 워커가 보고한 kind 를 쓴다.
export function formatCurrentJobKind(job: WorkerCurrentJob): string {
  if (job.subject === null) return formatJobKind(job.kind);
  const jobKind = job.subject.job_kind;
  const diagnosticKind = diagnosticJobKinds.find((kind) => kind === jobKind);
  return diagnosticKind === undefined
    ? formatJobKind(jobKind)
    : diagnosticJobKindLabel(diagnosticKind);
}

// uuid 앞 8자만 표시한다(테이블 폭 절약). 운영자는 이 prefix로 로그/DB를 조회한다.
export function shortUuid(uuid: string): string {
  return uuid.length > 8 ? uuid.slice(0, 8) : uuid;
}

// Worker id 는 pod 이름이다. 앞부분은 Deployment 이름이라 Worker 끼리 같다. 끝부분이 Worker 를
// 구분한다. 그래서 좁은 셀에서는 끝 16자만 표시한다. 호출부는 전체 이름을 title 로 함께 둔다.
export function workerIdTail(workerId: string): string {
  return workerId.length > 20 ? `…${workerId.slice(-16)}` : workerId;
}

// 시작 시각부터 nowMs까지 경과 시간. 파싱할 수 없으면 null 이다(호출부가 생략한다).
//
// 이 함수는 반드시 파서를 거쳐야 한다. 두 시각의 차를 구하는 다른 자리와 다르게, 이 함수는
// 파싱한 값에서 Date.now()를 뺀다. 그래서 오파싱한 offset이 상쇄되지 않는다. zone 없는
// 문자열을 로컬로 읽으면 방금 시작한 작업이 "9h 3m 처리 중"으로 보인다.
export function formatElapsedSince(isoTimestamp: string, nowMs: number): string | null {
  const startedMs = parseUtcMs(isoTimestamp);
  if (startedMs === null) {
    return null;
  }
  return formatDuration(nowMs - startedMs);
}

// 서버가 보낸 보고 경과 초 → "12s ago". 유한수가 아니면 빈칸 규약("-")을 따른다.
export function formatReportedAge(ageSeconds: number): string {
  if (!Number.isFinite(ageSeconds)) {
    return "-";
  }
  return `${formatDuration(ageSeconds * 1000)} ago`;
}

// 보고 시각 → 뷰어 타임존 절대 시각. "11h ago" 상대값 셀의 hover title 로 쓴다.
// 운영자가 이 값을 다른 리전 기록과 대조한다. 그래서 초와 뷰어 offset 까지 표시한다.
export function formatReportedAtAbsolute(isoTimestamp: string): string {
  return formatDateTimeSeconds(parseUtcMs(isoTimestamp));
}

// scratch 디스크 사용률 + 여유 용량을 한 줄로 표시한다. status blob에 disk가 없으면 "-".
export function formatDiskUsage(disk: WorkerDiskUsage | null): string {
  if (disk === null) {
    return "-";
  }
  return `${Math.round(disk.used_percent)}% · ${formatBytes(disk.free_bytes)} free`;
}

// 1분 load average(소수 2자리). loadavg가 없으면 "-".
export function formatLoad(loadavg: WorkerLoadAvg | null): string {
  if (loadavg === null || !Number.isFinite(loadavg["1m"])) {
    return "-";
  }
  return loadavg["1m"].toFixed(2);
}

// 세 구간 load average를 한 줄로 표시한다. 목록은 1m만 쓴다. 상세는 추세를 보려고 셋을 모두 보여준다.
export function formatLoadAverages(loadavg: WorkerLoadAvg | null): string {
  if (loadavg === null) {
    return "-";
  }
  return (["1m", "5m", "15m"] as const)
    .map((window) => {
      const value = loadavg[window];
      return `${window} ${Number.isFinite(value) ? value.toFixed(2) : "-"}`;
    })
    .join(" · ");
}

// 사용 중 / 전체 용량. 목록의 사용률 요약과 다르게 상세는 절대 바이트를 함께 보여준다.
export function formatDiskCapacity(disk: WorkerDiskUsage | null): string {
  if (disk === null) {
    return "-";
  }
  return `${formatBytes(disk.used_bytes)} / ${formatBytes(disk.total_bytes)}`;
}

// scratch 항목의 마지막 변경 이후 경과. 서버가 초 단위로 보낸다. 그래서 ms로 바꿔 공통 포맷에 전달한다.
export function formatScratchEntryAge(ageSeconds: number): string {
  if (!Number.isFinite(ageSeconds)) {
    return "-";
  }
  return formatDuration(ageSeconds * 1000);
}

// total=null은 분모 미보고, 0은 실제 빈 작업량이다. 둘 다 비율을 추측하지 않는다.
export function formatJobProgress(progress: WorkerJobProgress | null | undefined): string {
  if (progress == null) return "미보고";
  const done = progress.done.toLocaleString();
  return progress.total === null
    ? `${done} ${progress.unit} · 전체 수 미보고`
    : `${done} / ${progress.total.toLocaleString()} ${progress.unit}`;
}

export function formatWorkerAssignments(values: readonly string[] | null, empty: string): string {
  return values === null ? "미보고" : values.length === 0 ? empty : values.join(", ");
}

/**
 * 워커가 선언한 저장 위치를 화면 이름으로 적는다.
 *
 * 빈 목록은 미보고다. 서버는 이 값을 보고하지 않는 워커(옛 워커)에 빈 목록을 준다. 화면이 모르는
 * 값은 받은 그대로 적는다.
 *
 * Returns:
 *   예) `["gcp_storage", "azure_blob"]` → `"GCS, Azure"`, `[]` → `"미보고"`
 */
export function formatWorkerStorageBackends(values: readonly string[]): string {
  if (values.length === 0) return "미보고";
  return values.map(storageBackendLabel).join(", ");
}
